import { evaluateSortValue } from './sortUtils';

/**
 * Prepares a filter object based on the provided parameters.
 *
 * @param {Object} params - Input parameters containing filter values.
 * @param {string} [params.employeeId] - Employee ID (optional).
 * @param {string} [params.company] - Company ID (optional).
 *
 * @returns {Object} An object containing the prepared filter values.
 */
export function prepareRequestFilters(params) {
  return {
    employee: params.employeeId,
    company: params.company,
  };
}

/**
 * Prepares a sorting object based on the provided parameters.
 *
 * @param {Object} params - Input parameters containing sorting values.
 * @param {string} [params.sortCreatedAt] - Sorting order for the creation date (e.g., "asc" or "desc").
 * @param {string} [params.sortTypeOfLeave] - Sorting order for the creation date (e.g., "asc" or "desc").
 *
 * @returns {Object} An object containing the prepared sorting values.
 */
export function prepareRequestSorters(params) {
  return {
    createdAt: evaluateSortValue(params.sortCreatedAt),
    typeOfLeave: evaluateSortValue(params.sortTypeOfLeave),
  };
}

/**
 * Prepares a filters object based on the provided params and config.
 *
 * @param {Object} params - The input parameters to be filtered.
 * @param {Object} [config={}] - Configuration object that defines how each param should be processed.
 * @param {'range'|'uppercaseArray'|'array'|'uppercase'|function} config[key] - The type or function to process each param key.
 *   - 'range': Expects an array of two elements, returns an object with `from` and `to`.
 *   - 'uppercaseArray': Expects an array or string, converts all elements to uppercase.
 *   - 'array': Ensures the value is an array.
 *   - 'uppercase': Converts the value to uppercase.
 *   - function: Custom function to process the value.
 * @returns {Object} The processed filters object.
 */
export function prepareFilters(params, config = {}) {
  const filters = {};

  for (const key in config) {
    const type = config[key];
    const value = params[key];

    if (type === 'range') {
      if (Array.isArray(value) && value.length === 2) {
        const [from, to] = value;
        filters[key] = { from, to };
      }
    } else if (type === 'uppercaseArray') {
      if (value?.length > 0) {
        filters[key] = Array.isArray(value)
          ? value.map((v) => v.toUpperCase())
          : [value.toUpperCase()];
      }
    } else if (type === 'array') {
      filters[key] = Array.isArray(value) ? value : value ? [value] : undefined;
    } else if (typeof type === 'function') {
      filters[key] = type(value);
    } else if (type === 'uppercase') {
      filters[key] = value?.toUpperCase() ?? undefined;
    } else if (type === 'datesFromTo') {
      if (Array.isArray(value) && value.length === 2) {
        const [from, to] = value;
        filters['dateFrom'] = from;
        filters['dateTo'] = to;
      }
    } else {
      filters[key] = value ?? undefined;
    }
  }

  return filters;
}

/**
 * Prepares a new object with sorting values based on the provided parameters and a mapping.
 *
 * @param {Object} params - The source object containing sorting parameters.
 * @param {Object} mapping - An object mapping output keys to parameter keys in `params`.
 * @returns {Object} An object where each key from the mapping is assigned a value
 *                   obtained by evaluating the corresponding parameter value.
 */
export function prepareSorters(params, mapping) {
  const result = {};
  for (const [key, paramKey] of Object.entries(mapping)) {
    result[key] = evaluateSortValue(params[paramKey]);
  }
  return result;
}

/**
 * Prepares pagination parameters by parsing the offset and limit values from the input params object.
 *
 * @function
 * @param {Object} params - The parameters object containing pagination info.
 * @param {string|number} [params.offset] - The offset value as a string or number.
 * @param {string|number} [params.limit] - The limit value as a string or number.
 * @returns {Object} An object with parsed `offset` and `limit` as numbers, or `undefined` if not provided.
 */
export function preparePagination(params) {
  return {
    offset: params.offset ? parseInt(params.offset, 10) : undefined,
    limit: params.limit ? parseInt(params.limit, 10) : undefined,
  };
}

/**
 * Resolves conflicts in an object based on specified rules.
 *
 * For each rule, if more than one key from `rule.keys` is present in the object,
 * only the key specified by `rule.priority` is kept; the others are removed.
 * A key is considered present if its value is not `undefined` (or, if it's an array, if it has a length > 0).
 *
 * @example
 * const filters = {
 *   company: 'all',
 *   companies: ['1', '2'],
 * };
 *
 * const conflictRules = [
 *   { keys: ['company', 'companies'], priority: 'companies' },
 * ];
 *
 * const resolved = resolveConflicts(filters, conflictRules);
 *
 * @param {Object} obj - The object to resolve conflicts in.
 * @param {Array<{keys: string[], priority: string}>} rules - Array of conflict rules.
 * @returns {Object} A new object with conflicts resolved according to the rules.
 */
export const resolveConflicts = (obj, rules) => {
  const result = { ...obj };

  for (const rule of rules) {
    const presentKeys = rule.keys.filter((key) => {
      const val = result[key];
      return Array.isArray(val) ? val.length > 0 : val !== undefined;
    });

    if (presentKeys.length > 1) {
      for (const key of rule.keys) {
        if (key !== rule.priority && key in result) {
          delete result[key];
        }
      }
    }
  }

  return result;
};

/**
 * Encodes a string for use in a URL query parameter, replacing spaces with '+' instead of '%20'.
 *
 * @param {string} str - The string to encode.
 * @returns {string} The encoded string with spaces replaced by '+'.
 */
export const customUrlEncode = (str) => {
  return encodeURIComponent(str).replace(/%20/g, '+');
};
